// The rules that decide which of the reader's moves count as a blunder or a
// miss, and when a position counts as won and then thrown away. They have
// defaults (the app's rules until 2026-10-05) and can be changed by the user
// in the settings (plugin `settings-mistakes`), which the `mistakeRules` API
// serves. Every reader of the scan's verdicts takes them from there: the
// review (`GameScan`) and the per-game facts the dashboards count.

/** The registry name of the mistake rules' provider. */
export const MISTAKE_RULES_API = 'mistakeRules';

/** All values in pawns, from the mover's side. */
export interface MistakeRules {
  /** A move that gives up at least this much against the engine's best line is a blunder. */
  blunderPawns: number;
  /** A miss: the best line was at least this far ahead of the second best, and was not played. */
  missGapPawns: number;
  /** After a miss the mover still stands at least this well (usually negative); worse than that, the move is a blunder. */
  missHoldPawns: number;
  /** Being this far ahead counts as a winning position. */
  winningPawns: number;
  /** A winning position is thrown away when a blunder or miss leaves the mover under this. */
  heldPawns: number;
}

export type MistakeRule = keyof MistakeRules;

export const DEFAULT_MISTAKE_RULES: Readonly<MistakeRules> = Object.freeze({
  blunderPawns: 1,
  missGapPawns: 1,
  missHoldPawns: -1,
  winningPawns: 2,
  heldPawns: 0.5,
});

/** What each rule may be set to, in pawns, and the step of its input. */
export const MISTAKE_RULE_LIMITS: Readonly<Record<MistakeRule, { min: number; max: number; step: number }>> = Object.freeze({
  blunderPawns: { min: 0.3, max: 5, step: 0.1 },
  missGapPawns: { min: 0.3, max: 5, step: 0.1 },
  missHoldPawns: { min: -5, max: 0, step: 0.1 },
  winningPawns: { min: 0.5, max: 9, step: 0.1 },
  heldPawns: { min: -2, max: 3, step: 0.1 },
});

export const MISTAKE_RULE_NAMES: readonly MistakeRule[] = ['blunderPawns', 'missGapPawns', 'missHoldPawns', 'winningPawns', 'heldPawns'];

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * Rules made safe: each value a number within its limits, rounded to a
 * tenth of a pawn, the default where missing or not a number; and the held
 * line kept below the winning line, so "thrown away" can happen.
 */
export function normalizeMistakeRules(input: Partial<Record<MistakeRule, unknown>> | undefined): MistakeRules {
  const rules = { ...DEFAULT_MISTAKE_RULES };
  for (const name of MISTAKE_RULE_NAMES) {
    const raw = input?.[name];
    const value = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : NaN;
    if (!Number.isFinite(value)) continue;
    const { min, max } = MISTAKE_RULE_LIMITS[name];
    rules[name] = round1(Math.min(max, Math.max(min, value)));
  }
  if (rules.heldPawns >= rules.winningPawns) rules.heldPawns = round1(rules.winningPawns - 0.5);
  return rules;
}

/** Whether two sets of rules are the same. */
export function sameMistakeRules(a: MistakeRules, b: MistakeRules): boolean {
  return MISTAKE_RULE_NAMES.every((name) => a[name] === b[name]);
}

/** Whether the rules are the defaults. */
export function isDefaultMistakeRules(rules: MistakeRules): boolean {
  return sameMistakeRules(rules, DEFAULT_MISTAKE_RULES);
}

/** The current rules and their changes: what the `mistakeRules` API serves. */
export interface MistakeRulesSource {
  /** The rules now: the defaults until the saved ones are read. */
  get(): MistakeRules;
  /** Settles once the saved rules are read (or found missing). */
  ready(): Promise<MistakeRules>;
  /** Hears every change of the rules. Returns the unsubscribe. */
  subscribe(listener: (rules: MistakeRules) => void): () => void;
  /** Normalizes, stores and announces new rules. */
  save(rules: Partial<MistakeRules>): Promise<MistakeRules>;
}
